import React from 'react';
import { CircleSeriesNode, CircleTopicFormat } from '@modernhealth/circles-graphql';
import { CircleCard, CircleCardLink, SeriesAccessType } from '@modernhealth/circles-web-components';
import { filterNullish } from '@modernhealth/filter-nullish';
import { BodyTextXSmall, ComponentTextSmall } from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { Link } from 'react-router-dom';

import { useTranslation } from 'src/shared/hooks/formatMessage';

import { formatCircleFormat } from './utils/formatCircleFormat';
import { formatSeriesAccessType } from '../utils/formatSeriesAccessType';

export interface CircleStylizedCardProps {
  session?: {
    facilitators: Array<{ photo?: string | null } | null>;
    topic?: {
      name: string;
      slug?: string | null;
      format: CircleTopicFormat;
      series?: Partial<CircleSeriesNode>;
      duration?: number;
    };
  };
  topic: {
    name: string;
    slug?: string | null;
    format: CircleTopicFormat;
    series?: Partial<CircleSeriesNode>;
    duration?: number;
  };
}

const CardLink: React.FC<{ slug: string; name: string; size?: number }> = ({
  slug,
  name,
  size,
}) => (
  <Link
    css={{
      fontSize: size ?? 'inherit',
      fontWeight: size ? 500 : 400,
      color: size ? colors.gray65 : colors.gray85,
      '&:hover': {
        color: colors.gray85,
      },
      '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        right: 0,
        left: 0,
      },
    }}
    to={`/circle/${slug}`}
    aria-describedby={`topic-id-${slug}`}
  >
    {name}
  </Link>
);

export const CircleStylizedCard: React.FC<CircleStylizedCardProps> = ({
  session,
  topic,
  children,
}) => {
  const photos = filterNullish(session?.facilitators.map(facilitator => facilitator?.photo) ?? []);
  const { $t } = useTranslation();
  const isMemberOnly = topic?.series?.accessType === SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY;
  const sessionFormat = formatCircleFormat(topic.format);
  return (
    <CircleCard
      format={topic.format}
      eyebrow={
        isMemberOnly && (
          <BodyTextXSmall as="span" fontColor="violet55" css={{ fontWeight: 500 }}>
            {$t(formatSeriesAccessType(SeriesAccessType.MODERN_HEALTH_MEMBERS_ONLY))}
          </BodyTextXSmall>
        )
      }
      photos={photos}
      header={<CardLink name={topic.name} slug={topic.slug!} />}
      body={
        <ComponentTextSmall marginTop={8}>
          {sessionFormat}
          {` • ${topic.duration} min`}
        </ComponentTextSmall>
      }
    >
      <CircleCardLink component={<span aria-hidden css={{ marginTop: 8 }} />}>
        {children}
      </CircleCardLink>
    </CircleCard>
  );
};
